<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { ActionButton, ErrorState } from '@taler/ui'
import { KpiChangeIndicator, meaningfulPercent, type PercentChange } from '@/features/dashboard'
import type { CarEvent, CarStatistics, CarStatisticsPeriod } from '../internal/types.ts'
import {
  countChangeRatio,
  type Locale,
  displaysAsZero,
  formatCount,
  formatDate,
  moneyChangeRatio,
  moneyProportion,
  type Money,
  PLUS_SIGN,
  signOfMoney,
  subtractMoney,
  zeroMoney,
} from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CarEventIcon from '@/features/garage/components/CarEventIcon.vue'
import CarStatIcon from '@/features/garage/components/CarStatIcon.vue'
import { categoryOf, EVENT_CATEGORIES, type EventCategory } from '../internal/car-presentation.ts'
const props = defineProps<{
  statistics: CarStatistics | undefined
  period: CarStatisticsPeriod
  events: readonly CarEvent[]
  accountNames: ReadonlyMap<string, string>
  year: number
  currentYear: number
  loading: boolean
  failed: boolean
  retrying?: boolean | undefined
}>()
const emit = defineEmits<{ retry: []; 'update:year': [year: number] }>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as Locale)

const periodLabel = computed(() => {
  return `${formatDate(props.period.from, currentLocale.value)} – ${formatDate(props.period.until, currentLocale.value)}`
})

// A figure the server could not compute, or one with no base to compare against, has no percentage.
function comparisonOf(base: Money | undefined, current: Money | undefined): PercentChange | undefined {
  if (base === undefined || current === undefined) return undefined
  const percent = meaningfulPercent(moneyChangeRatio(current, base))
  if (percent === undefined) return undefined
  return { direction: signOfMoney(subtractMoney(current, base)), percent }
}

// The distance driven is a count, not money: its change is a percentage and a direction only.
function distanceComparisonOf(
  base: number | undefined,
  current: number | undefined,
): PercentChange | undefined {
  if (base === undefined || current === undefined) return undefined
  const percent = meaningfulPercent(countChangeRatio(current, base))
  if (percent === undefined) return undefined
  return { direction: current > base ? 1 : current < base ? -1 : 0, percent }
}

const comparisons = computed(() => {
  const statistics = props.statistics
  if (statistics === undefined) return undefined
  const previous = statistics.previousPeriod.figures
  return {
    distanceKm: distanceComparisonOf(previous.distanceKm, statistics.distanceKm),
    totalSpend: comparisonOf(previous.totalSpend, statistics.totalSpend),
    spendThisYear: comparisonOf(previous.spendThisYear, statistics.spendThisYear),
    costPerKm: comparisonOf(previous.costPerKm, statistics.costPerKm),
  }
})

const breakdown = computed(() => {
  const total = props.statistics?.totalSpend
  return (props.statistics?.breakdown ?? [])
    .filter((row) => !displaysAsZero(row.amount))
    .map((row) => {
      const share =
        total === undefined || displaysAsZero(total)
          ? 0
          : moneyProportion(row.amount, zeroMoney(total.currency), total)
      return {
        accountId: row.accountId,
        label: props.accountNames.get(row.accountId) ?? row.accountId,
        amount: row.amount,
        percent: formatCount(Math.round(share * 100), currentLocale.value),
      }
    })
})

const eventCounts = computed(() =>
  EVENT_CATEGORIES.map((category: EventCategory) => ({
    category,
    count: formatCount(
      props.events.filter((event) => categoryOf(event.type) === category).length,
      currentLocale.value,
    ),
  })),
)

const LABEL_CLASS = 'text-[color:var(--color-text-muted)]'
const BOX_CLASS =
  'flex flex-col gap-2 rounded-[var(--radius-control)] border border-[color:var(--color-border)] p-4'
const TILE_CLASS =
  'flex flex-col gap-2 rounded-[var(--radius-control)] border border-[color:var(--color-border)] p-3'
</script>

<template>
  <section
    class="@container flex flex-col gap-4 rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-4"
  >
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
        {{ t('garage.stats.title') }}
      </h2>
      <div class="flex items-center gap-1" role="group" :aria-label="t('garage.stats.periodLabel')">
        <span
          class="flex items-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] border border-[color:var(--color-border)] px-3 py-2 tabular-nums text-[color:var(--color-text)]"
        >
          <CarStatIcon :paths="['M2.5 3.5h11v10h-11z', 'M2.5 6.5h11M5.5 2v2.5M10.5 2v2.5']" />
          {{ periodLabel }}
        </span>
        <ActionButton
          variant="secondary"
          :aria-label="t('garage.stats.previousPeriod')"
          @click="emit('update:year', year - 1)"
        >
          <span aria-hidden="true">‹</span>
        </ActionButton>
        <ActionButton
          variant="secondary"
          :aria-label="t('garage.stats.nextPeriod')"
          :disabled="year >= currentYear"
          @click="emit('update:year', year + 1)"
        >
          <span aria-hidden="true">›</span>
        </ActionButton>
      </div>
    </div>

    <ErrorState
      v-if="failed"
      :title="t('garage.errorTitle')"
      :body="t('garage.errorBody')"
      :retry-label="t('garage.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />
    <p v-else-if="statistics === undefined && loading" role="status">{{ t('app.loading') }}</p>
    <p v-else-if="statistics === undefined" :class="LABEL_CLASS">{{ t('garage.stats.noStatistics') }}</p>

    <template v-else>
      <div class="grid grid-cols-2 gap-3 @[40rem]:grid-cols-4">
        <div :class="TILE_CLASS">
          <span class="flex items-center gap-2 whitespace-nowrap" :class="LABEL_CLASS">
            <CarStatIcon :paths="['M2.5 11.5a5.5 5.5 0 1 1 11 0', 'M8 11.5l3-3.5']" />
            {{ t('garage.stats.currentMileage') }}
          </span>
          <span
            class="text-[length:var(--text-step-5)] font-semibold tabular-nums text-[color:var(--color-text)]"
          >
            {{ formatCount(statistics.currentMileageKm, currentLocale) }} km
          </span>
          <span
            v-if="statistics.distanceKm !== undefined && statistics.distanceKm > 0"
            class="text-[color:var(--color-positive)] tabular-nums"
          >
            {{ PLUS_SIGN }}{{ formatCount(statistics.distanceKm, currentLocale) }} km
          </span>
          <span v-else-if="statistics.distanceKm === 0" class="tabular-nums" :class="LABEL_CLASS">
            {{ formatCount(0, currentLocale) }} km
          </span>
          <KpiChangeIndicator
            v-if="comparisons?.distanceKm"
            :change="comparisons.distanceKm"
            favourable="up"
            :percent-digits="0"
            :caption="t('garage.stats.vsLastYear')"
          />
        </div>
        <div :class="TILE_CLASS">
          <span class="flex items-center gap-2 whitespace-nowrap" :class="LABEL_CLASS">
            <CarStatIcon
              :paths="[
                'M3 3.8a5 2 0 1 0 10 0a5 2 0 1 0-10 0',
                'M3 3.8v8.4c0 1.1 2.2 2 5 2s5-.9 5-2V3.8',
                'M3 8c0 1.1 2.2 2 5 2s5-.9 5-2',
              ]"
            />
            {{ t('garage.stats.totalSpend') }}
          </span>
          <MoneyAmount :value="statistics.totalSpend" size="large" />
          <KpiChangeIndicator
            v-if="comparisons?.totalSpend"
            :change="comparisons.totalSpend"
            favourable="down"
            :percent-digits="0"
            :caption="t('garage.stats.vsLastYear')"
          />
        </div>
        <div :class="TILE_CLASS">
          <span class="flex items-center gap-2 whitespace-nowrap" :class="LABEL_CLASS">
            <CarStatIcon :paths="['M2.5 3.5h11v10h-11z', 'M2.5 6.5h11M5.5 2v2.5M10.5 2v2.5']" />
            {{ t('garage.stats.spendInYear', { year }) }}
          </span>
          <MoneyAmount :value="statistics.spendThisYear" size="large" />
          <KpiChangeIndicator
            v-if="comparisons?.spendThisYear"
            :change="comparisons.spendThisYear"
            favourable="down"
            :percent-digits="0"
            :caption="t('garage.stats.vsLastYear')"
          />
        </div>
        <div :class="TILE_CLASS">
          <span class="flex items-center gap-2 whitespace-nowrap" :class="LABEL_CLASS">
            <CarStatIcon :paths="['M3 13.5V8M8 13.5V3M13 13.5V6']" />
            {{ t('garage.stats.costPerKm') }}
          </span>
          <span v-if="statistics.costPerKm !== undefined" class="flex items-baseline gap-1">
            <MoneyAmount :value="statistics.costPerKm" size="large" />
            <span :class="LABEL_CLASS">/ km</span>
          </span>
          <span v-else :class="LABEL_CLASS">{{
            statistics.distanceKm === undefined
              ? t('garage.stats.odometerConflict')
              : t('garage.stats.costPerKmUnknown')
          }}</span>
          <KpiChangeIndicator
            v-if="comparisons?.costPerKm"
            :change="comparisons.costPerKm"
            favourable="down"
            :percent-digits="0"
            :caption="t('garage.stats.vsLastYear')"
          />
        </div>
      </div>

      <div class="grid items-start gap-3 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div :class="BOX_CLASS">
          <h3 class="font-semibold text-[color:var(--color-text)]">{{ t('garage.stats.breakdownTitle') }}</h3>
          <ul class="flex flex-col">
            <li
              v-for="row in breakdown"
              :key="row.accountId"
              class="flex items-center justify-between gap-3 border-b border-[color:var(--color-border)] py-2 last:border-0"
            >
              <span class="text-[color:var(--color-text)]">{{ row.label }}</span>
              <span class="flex items-baseline gap-3">
                <MoneyAmount :value="row.amount" />
                <span class="w-10 text-right tabular-nums" :class="LABEL_CLASS">{{ row.percent }}%</span>
              </span>
            </li>
          </ul>
        </div>

        <div :class="BOX_CLASS">
          <h3 class="font-semibold text-[color:var(--color-text)]">{{ t('garage.stats.eventCounts') }}</h3>
          <ul class="flex flex-col">
            <li
              v-for="row in eventCounts"
              :key="row.category"
              class="flex items-center justify-between gap-3 border-b border-[color:var(--color-border)] py-2 last:border-0"
            >
              <span class="flex items-center gap-2 whitespace-nowrap text-[color:var(--color-text)]">
                <CarEventIcon :category="row.category" />
                {{ t(`garage.categories.${row.category}`) }}
              </span>
              <span class="tabular-nums text-[color:var(--color-text)]">{{ row.count }}</span>
            </li>
          </ul>
        </div>
      </div>
    </template>
  </section>
</template>
